import type { ReactElement } from 'react';
import type { ShoppingListItemDto } from '../../api/shoppingTypes.js';

export interface ItemRowProps {
  item: ShoppingListItemDto;
  onToggle: (id: number, value: boolean) => void;
  onRemove: (id: number) => void;
  isDragging?: boolean;
  dragHandleProps?: Record<string, unknown>;
}

export function ItemRow({
  item,
  onToggle,
  onRemove,
  isDragging,
  dragHandleProps,
}: ItemRowProps): ReactElement {
  return (
    <div
      className="flex items-center gap-2 py-1"
      style={{
        color: 'var(--text-main)',
        textDecoration: item.is_checked ? 'line-through' : 'none',
        opacity: isDragging ? 0.5 : 1,
        background: 'transparent',
      }}
      data-testid={`shopping-item-${item.id}`}
    >
      <span
        aria-label={`Drag to reorder ${item.name}`}
        {...dragHandleProps}
        className="select-none touch-none text-base"
        style={{
          color: 'var(--text-muted)',
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
        data-testid={`shopping-item-${item.id}-handle`}
      >
        ⠿
      </span>
      <input
        type="checkbox"
        checked={item.is_checked}
        onChange={(e) => onToggle(item.id, e.target.checked)}
        className="h-4 w-4"
        style={{ touchAction: 'manipulation' }}
        data-testid={`shopping-item-${item.id}-check`}
      />
      <span className="flex-1 text-sm">{item.name}</span>
      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {formatAmount(item.amount)} {item.unit}
      </span>
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        aria-label={`Remove ${item.name}`}
        className="flex h-7 w-7 items-center justify-center rounded border text-sm"
        style={{
          borderColor: 'var(--border)',
          color: 'var(--text-muted)',
          background: 'transparent',
          touchAction: 'manipulation',
        }}
        data-testid={`shopping-item-${item.id}-remove`}
      >
        🗑
      </button>
    </div>
  );
}

function formatAmount(n: number): string {
  return String(Math.round(n * 100) / 100);
}
